/**
 * Centralized store for managing tenant selection across all configure pages
 * This ensures consistent tenant selection behavior across Primary Classification,
 * Sub-Categories, Document Types, Models, and Extraction Fields pages
 */

import { get } from '@/utils/api'

class TenantSelectionStore {
  constructor() {
    this.selectedTenantId = ''
    this.selectedTenantUsesBedrock = false
    this.selectedTenantUsesTextract = false
    this.userRole = ''
    this.subscribers = []
    this.loadingTenantAiMethod = false

    // Initialize from localStorage and user role
    this.initialize()

    // Make this available globally for debugging
    window.tenantSelectionStore = this
  }

  initialize() {
    // Get user role
    try {
      const userRolesString = localStorage.getItem('UserRoles')
      if (userRolesString) {
        const userRoles = JSON.parse(userRolesString)
        this.userRole = userRoles[0]?.role?.roleName || ''
      }
    } catch (e) {
      this.userRole = localStorage.getItem('userRole') || ''
    }

    // Initialize tenant selection based on user role
    if (this.userRole === 'Super Admin') {
      this.selectedTenantId = localStorage.getItem('globalSelectedTenantId') || 'master'
      this.selectedTenantUsesBedrock = localStorage.getItem('selectedTenantUsesBedrock') === 'true'
      this.selectedTenantUsesTextract = localStorage.getItem('selectedTenantUsesTextract') === 'true'

      console.log('Store: Super Admin initialization - tenantId:', this.selectedTenantId, 'bedrock:', this.selectedTenantUsesBedrock, 'textract:', this.selectedTenantUsesTextract)

      // Dispatch event immediately for Configure.vue to detect initial state
      if (this.selectedTenantUsesBedrock) {
        window.dispatchEvent(
          new CustomEvent('tenantBedrockStatusChanged', {
            detail: {
              selectedTenantUsesBedrock: this.selectedTenantUsesBedrock,
              selectedTenantId: this.selectedTenantId,
            },
          })
        )
      }

      // Dispatch event for Textract status
      if (this.selectedTenantUsesTextract) {
        window.dispatchEvent(
          new CustomEvent('tenantTextractStatusChanged', {
            detail: {
              selectedTenantUsesTextract: this.selectedTenantUsesTextract,
              selectedTenantId: this.selectedTenantId,
            },
          })
        )
      }
    } else {
      // For regular users (Tenant Admin), use their default tenant
      this.selectedTenantId = localStorage.getItem('userTenantId') || ''
      console.log('Store: Regular user initialization - tenantId:', this.selectedTenantId)

      // Always restore bedrock status from localStorage first
      const cachedBedrock = localStorage.getItem('selectedTenantUsesBedrock')
      const cachedTextract = localStorage.getItem('selectedTenantUsesTextract')
      console.log('Store: Cached bedrock for regular user:', cachedBedrock)
      console.log('Store: Cached textract for regular user:', cachedTextract)

      if (cachedBedrock !== null) {
        this.selectedTenantUsesBedrock = cachedBedrock === 'true'
        console.log('Store: Using cached bedrock status:', this.selectedTenantUsesBedrock)
      } else {
        console.log('Store: No cached bedrock status, checking alternatives')

        // Try to get from legacy TenantDetails as fallback
        let foundInTenantDetails = false
        try {
          const tenantDetails = localStorage.getItem('TenantDetails')
          if (tenantDetails) {
            const tenantData = JSON.parse(tenantDetails)
            console.log('Store: TenantDetails found with', tenantData.length, 'configs')
            const aiMethodConfig = tenantData.find((config) => config.key === 'ai_method')
            if (aiMethodConfig) {
              this.selectedTenantUsesBedrock = aiMethodConfig.value === 'Bedrock Data Automation'
              this.selectedTenantUsesTextract = aiMethodConfig.value === 'Textract'
              // Cache this for future use
              localStorage.setItem('selectedTenantUsesBedrock', this.selectedTenantUsesBedrock.toString())
              localStorage.setItem('selectedTenantUsesTextract', this.selectedTenantUsesTextract.toString())
              console.log('Store: Restored bedrock status from legacy TenantDetails:', this.selectedTenantUsesBedrock)
              console.log('Store: Restored textract status from legacy TenantDetails:', this.selectedTenantUsesTextract)
              foundInTenantDetails = true
            } else {
              console.log('Store: No ai_method config found in TenantDetails')
            }
          } else {
            console.log('Store: No TenantDetails found in localStorage')
          }
        } catch (error) {
          console.error('Store: Error reading legacy TenantDetails:', error)
        }

        // If still no value and we have a tenant ID, check API immediately for regular users
        if (!foundInTenantDetails && this.selectedTenantId) {
          console.log('Store: No cached data found, checking API for tenant:', this.selectedTenantId)
          // For regular users, we need to check their AI method on every page load
          // since they don't have a selection mechanism like Super Admin
          this.checkTenantAiMethod(this.selectedTenantId).catch((error) => {
            console.error('Store: Failed to check tenant AI method:', error)
            // Default to false if API fails
            this.selectedTenantUsesBedrock = false
            this.selectedTenantUsesTextract = false
            localStorage.setItem('selectedTenantUsesBedrock', 'false')
            localStorage.setItem('selectedTenantUsesTextract', 'false')
          })
        }
      }

      // Also restore textract status from localStorage
      if (cachedTextract !== null) {
        this.selectedTenantUsesTextract = cachedTextract === 'true'
        console.log('Store: Using cached textract status:', this.selectedTenantUsesTextract)
      }
    }

    console.log('Tenant store initialized:', {
      userRole: this.userRole,
      selectedTenantId: this.selectedTenantId,
      selectedTenantUsesBedrock: this.selectedTenantUsesBedrock,
      selectedTenantUsesTextract: this.selectedTenantUsesTextract,
      cachedBedrock: localStorage.getItem('selectedTenantUsesBedrock'),
      cachedTextract: localStorage.getItem('selectedTenantUsesTextract'),
    })
  }

  /**
   * Subscribe to tenant selection changes
   * @param {Function} callback - Function to call when tenant selection changes
   * @returns {Function} Unsubscribe function
   */
  subscribe(callback) {
    this.subscribers.push(callback)

    // Return unsubscribe function
    return () => {
      const index = this.subscribers.indexOf(callback)
      if (index > -1) {
        this.subscribers.splice(index, 1)
      }
    }
  }

  /**
   * Notify all subscribers of changes
   */
  notify() {
    this.subscribers.forEach((callback) => {
      try {
        callback({
          selectedTenantId: this.selectedTenantId,
          selectedTenantUsesBedrock: this.selectedTenantUsesBedrock,
          selectedTenantUsesTextract: this.selectedTenantUsesTextract,
          userRole: this.userRole,
          loadingTenantAiMethod: this.loadingTenantAiMethod,
        })
      } catch (error) {
        console.error('Error in tenant selection subscriber:', error)
      }
    })
  }

  /**
   * Get current tenant selection state
   */
  getState() {
    return {
      selectedTenantId: this.selectedTenantId,
      selectedTenantUsesBedrock: this.selectedTenantUsesBedrock,
      selectedTenantUsesTextract: this.selectedTenantUsesTextract,
      userRole: this.userRole,
      loadingTenantAiMethod: this.loadingTenantAiMethod,
    }
  }

  /**
   * Set selected tenant ID and check its AI method
   */
  async setSelectedTenantId(tenantId) {
    console.log('\ud83c\udfe2 Store: setSelectedTenantId called with:', tenantId, 'current:', this.selectedTenantId)

    if (this.selectedTenantId === tenantId) {
      console.log('Store: Tenant ID unchanged, skipping')
      return
    }

    // Update store state
    this.selectedTenantId = tenantId
    console.log('\ud83c\udfe2 Store: Updated selectedTenantId to:', this.selectedTenantId)

    // Persist to localStorage for browser refresh (only for persistence, not primary state)
    if (this.userRole === 'Super Admin') {
      localStorage.setItem('globalSelectedTenantId', tenantId)
    }

    // Check AI method for the new tenant
    await this.checkTenantAiMethod(tenantId)

    // Notify all subscribers of state change
    console.log('\ud83c\udfe2 Store: Notifying', this.subscribers.length, 'subscribers')
    this.notify()
  }

  /**
   * Check tenant AI method and update bedrock status
   */
  async checkTenantAiMethod(tenantId) {
    console.log('Store: checkTenantAiMethod called for tenant:', tenantId, 'type:', typeof tenantId)

    // If we already have a cached value and this is the same tenant, don't override unless necessary
    const cachedBedrock = localStorage.getItem('selectedTenantUsesBedrock')
    console.log('Store: Current cached bedrock status:', cachedBedrock)

    this.loadingTenantAiMethod = true
    this.notify()

    let tenantConfigs = []

    try {
      if (tenantId && String(tenantId) !== 'master' && tenantId !== '') {
        console.log('Store: Fetching tenant config for:', tenantId, '(valid tenant ID)')
        // Fetch tenant configs to determine AI method
        const response = await get(`/v1/tenant-configs/tenant/${tenantId}`)
        console.log('Store: API response:', response?.data)

        if (response?.data?.code === 200) {
          tenantConfigs = response.data.data || []
          console.log('Store: Tenant configs received:', tenantConfigs.length, 'configs')
          const aiMethodConfig = tenantConfigs.find((config) => config.key === 'ai_method')
          const aiMethod = aiMethodConfig?.value
          this.selectedTenantUsesBedrock = aiMethod === 'Bedrock Data Automation'
          this.selectedTenantUsesTextract = aiMethod === 'Textract'
          console.log('Store: Tenant', tenantId, 'AI method:', aiMethod, 'usesBedrock:', this.selectedTenantUsesBedrock, 'usesTextract:', this.selectedTenantUsesTextract)
        } else {
          console.log('Store: API call failed, response code:', response?.data?.code)
          // Don't override cached value if API fails
          if (cachedBedrock !== null) {
            this.selectedTenantUsesBedrock = cachedBedrock === 'true'
            console.log('Store: Keeping cached bedrock status due to API failure:', this.selectedTenantUsesBedrock)
          } else {
            this.selectedTenantUsesBedrock = false
            console.log('Store: No cache available, setting bedrock to false')
          }

          const cachedTextract = localStorage.getItem('selectedTenantUsesTextract')
          if (cachedTextract !== null) {
            this.selectedTenantUsesTextract = cachedTextract === 'true'
            console.log('Store: Keeping cached textract status due to API failure:', this.selectedTenantUsesTextract)
          } else {
            this.selectedTenantUsesTextract = false
            console.log('Store: No cache available, setting textract to false')
          }
        }
      } else {
        // For master tenant, always show non-Bedrock and non-Textract format
        this.selectedTenantUsesBedrock = false
        this.selectedTenantUsesTextract = false
        console.log('Store: Master/empty tenant detected:', tenantId, 'setting bedrock and textract to false')
      }

      // Store the bedrock and textract status in localStorage for all users
      localStorage.setItem('selectedTenantUsesBedrock', this.selectedTenantUsesBedrock.toString())
      localStorage.setItem('selectedTenantUsesTextract', this.selectedTenantUsesTextract.toString())
      console.log('Store: Saved bedrock status to localStorage:', this.selectedTenantUsesBedrock)
      console.log('Store: Saved textract status to localStorage:', this.selectedTenantUsesTextract)

      // Also update TenantDetails for backward compatibility with tenantUtils.js
      this.updateLegacyTenantDetails(tenantConfigs)

      // Always dispatch custom event for all users
      console.log('Store: Dispatching tenantBedrockStatusChanged event for all users')
      window.dispatchEvent(
        new CustomEvent('tenantBedrockStatusChanged', {
          detail: {
            selectedTenantUsesBedrock: this.selectedTenantUsesBedrock,
            selectedTenantId: this.selectedTenantId,
          },
        })
      )

      console.log('Store: Dispatching tenantTextractStatusChanged event for all users')
      window.dispatchEvent(
        new CustomEvent('tenantTextractStatusChanged', {
          detail: {
            selectedTenantUsesTextract: this.selectedTenantUsesTextract,
            selectedTenantId: this.selectedTenantId,
          },
        })
      )
    } catch (error) {
      console.error('Store: Error fetching tenant AI method:', error)
      this.selectedTenantUsesBedrock = false
      this.selectedTenantUsesTextract = false

      // Store error state in localStorage for all users
      localStorage.setItem('selectedTenantUsesBedrock', 'false')
      localStorage.setItem('selectedTenantUsesTextract', 'false')
    } finally {
      this.loadingTenantAiMethod = false
      console.log('Store: checkTenantAiMethod completed, notifying subscribers')
      this.notify()
    }
  }

  /**
   * Set tenant selection from popup result (for Super Admin)
   */
  async setTenantFromPopup(tenantData) {
    if (!tenantData || !tenantData.id) return

    this.selectedTenantId = tenantData.id
    this.selectedTenantUsesBedrock = tenantData.selectedTenantHasBedrock || false
    this.selectedTenantUsesTextract = tenantData.selectedTenantHasTextract || false

    // Store for persistence
    localStorage.setItem('selectedTenantUsesBedrock', this.selectedTenantUsesBedrock.toString())
    localStorage.setItem('selectedTenantUsesTextract', this.selectedTenantUsesTextract.toString())

    if (this.userRole === 'Super Admin') {
      localStorage.setItem('globalSelectedTenantId', tenantData.id)

      // Dispatch event to notify Configure.vue
      window.dispatchEvent(
        new CustomEvent('tenantBedrockStatusChanged', {
          detail: {
            selectedTenantUsesBedrock: this.selectedTenantUsesBedrock,
            selectedTenantId: this.selectedTenantId,
          },
        })
      )

      window.dispatchEvent(
        new CustomEvent('tenantTextractStatusChanged', {
          detail: {
            selectedTenantUsesTextract: this.selectedTenantUsesTextract,
            selectedTenantId: this.selectedTenantId,
          },
        })
      )
    }

    this.notify()
  }

  /**
   * Force update bedrock status (useful for external updates)
   */
  updateBedrockStatus(usesBedrock) {
    if (this.selectedTenantUsesBedrock === usesBedrock) return

    this.selectedTenantUsesBedrock = usesBedrock

    // Persist bedrock status for all users
    localStorage.setItem('selectedTenantUsesBedrock', usesBedrock.toString())

    if (this.userRole === 'Super Admin') {
      // Dispatch event to notify Configure.vue
      window.dispatchEvent(
        new CustomEvent('tenantBedrockStatusChanged', {
          detail: {
            selectedTenantUsesBedrock: usesBedrock,
            selectedTenantId: this.selectedTenantId,
          },
        })
      )
    }

    this.notify()
  }

  /**
   * Force update textract status (useful for external updates)
   */
  updateTextractStatus(usesTextract) {
    if (this.selectedTenantUsesTextract === usesTextract) return

    this.selectedTenantUsesTextract = usesTextract

    // Persist textract status for all users
    localStorage.setItem('selectedTenantUsesTextract', usesTextract.toString())

    if (this.userRole === 'Super Admin') {
      // Dispatch event to notify Configure.vue
      window.dispatchEvent(
        new CustomEvent('tenantTextractStatusChanged', {
          detail: {
            selectedTenantUsesTextract: usesTextract,
            selectedTenantId: this.selectedTenantId,
          },
        })
      )
    }

    this.notify()
  }

  /**
   * Reset to default state
   */
  reset() {
    this.selectedTenantId = this.userRole === 'Super Admin' ? 'master' : ''
    this.selectedTenantUsesBedrock = false
    this.selectedTenantUsesTextract = false
    this.loadingTenantAiMethod = false

    // Persist reset state for all users
    localStorage.setItem('selectedTenantUsesBedrock', 'false')
    localStorage.setItem('selectedTenantUsesTextract', 'false')

    if (this.userRole === 'Super Admin') {
      localStorage.setItem('globalSelectedTenantId', this.selectedTenantId)
    }

    this.notify()
    this.debugState('After reset')
  }

  /**
   * Debug method to log current state
   */
  debugState(context = '') {
    console.group(`🔍 Tenant Store Debug ${context}`)
    console.log('Store state:', {
      selectedTenantId: this.selectedTenantId,
      selectedTenantUsesBedrock: this.selectedTenantUsesBedrock,
      selectedTenantUsesTextract: this.selectedTenantUsesTextract,
      loadingTenantAiMethod: this.loadingTenantAiMethod,
      userRole: this.userRole,
      subscriberCount: this.subscribers.length,
    })
    console.log('localStorage state:', {
      globalSelectedTenantId: localStorage.getItem('globalSelectedTenantId'),
      selectedTenantUsesBedrock: localStorage.getItem('selectedTenantUsesBedrock'),
      selectedTenantUsesTextract: localStorage.getItem('selectedTenantUsesTextract'),
    })
    console.groupEnd()
  }

  /**
   * Get current store state for components
   * Pure state getter - no localStorage dependency
   */
  getSelectedTenantId() {
    return this.selectedTenantId
  }

  /**
   * Get full current state
   */
  getCurrentState() {
    return {
      selectedTenantId: this.selectedTenantId,
      selectedTenantUsesBedrock: this.selectedTenantUsesBedrock,
      selectedTenantUsesTextract: this.selectedTenantUsesTextract,
      loadingTenantAiMethod: this.loadingTenantAiMethod,
      userRole: this.userRole,
    }
  }

  /**
   * Initialize AI method for regular users (if not cached)
   * Should be called when components need the AI method info
   */
  async initializeRegularUserAiMethod() {
    if (this.userRole === 'Super Admin') return // Not needed for Super Admin

    console.log('Store: initializeRegularUserAiMethod called for tenant:', this.selectedTenantId)

    const cachedBedrock = localStorage.getItem('selectedTenantUsesBedrock')
    console.log('Store: Current cached status:', cachedBedrock)

    if (this.selectedTenantId) {
      if (cachedBedrock === null) {
        console.log('Store: No cache found, fetching AI method for tenant:', this.selectedTenantId)
        await this.checkTenantAiMethod(this.selectedTenantId)
      } else {
        console.log('Store: Found cached AI method:', cachedBedrock)
        // Make sure store state matches cache
        const cachedValue = cachedBedrock === 'true'
        if (this.selectedTenantUsesBedrock !== cachedValue) {
          console.log('Store: Store state mismatch, updating from cache')
          this.selectedTenantUsesBedrock = cachedValue
          this.notify() // Notify subscribers of the update
        }
      }
    } else {
      console.log('Store: No tenant ID available for regular user')
    }
  }

  /**
   * Update legacy TenantDetails localStorage for backward compatibility
   * Some components still use tenantUtils.js which reads from TenantDetails
   */
  updateLegacyTenantDetails(tenantConfigs) {
    try {
      if (tenantConfigs && tenantConfigs.length > 0) {
        localStorage.setItem('TenantDetails', JSON.stringify(tenantConfigs))
        console.log('Store: Updated legacy TenantDetails for backward compatibility')
      }
    } catch (error) {
      console.error('Store: Error updating legacy TenantDetails:', error)
    }
  }

  /**
   * Force refresh state and notify all subscribers
   * Useful for components that need to ensure they have the latest state
   */
  forceRefresh() {
    console.log('Store: Force refreshing state and notifying subscribers')

    // Re-read from localStorage in case something changed externally
    const cachedBedrock = localStorage.getItem('selectedTenantUsesBedrock')
    if (cachedBedrock !== null) {
      this.selectedTenantUsesBedrock = cachedBedrock === 'true'
      console.log('Store: Updated bedrock status from cache:', this.selectedTenantUsesBedrock)
    }

    const cachedTextract = localStorage.getItem('selectedTenantUsesTextract')
    if (cachedTextract !== null) {
      this.selectedTenantUsesTextract = cachedTextract === 'true'
      console.log('Store: Updated textract status from cache:', this.selectedTenantUsesTextract)
    }

    // Notify all subscribers
    this.notify()

    // Also dispatch the custom event
    window.dispatchEvent(
      new CustomEvent('tenantBedrockStatusChanged', {
        detail: {
          selectedTenantUsesBedrock: this.selectedTenantUsesBedrock,
          selectedTenantId: this.selectedTenantId,
        },
      })
    )

    window.dispatchEvent(
      new CustomEvent('tenantTextractStatusChanged', {
        detail: {
          selectedTenantUsesTextract: this.selectedTenantUsesTextract,
          selectedTenantId: this.selectedTenantId,
        },
      })
    )

    console.log('Store: Force refresh completed')
  }
}

// Create singleton instance
const tenantSelectionStore = new TenantSelectionStore()

// Debug method for global access
window.debugTenantStore = () => {
  tenantSelectionStore.debugState('Manual debug call')
}

// Expose store for debugging
window.tenantSelectionStore = tenantSelectionStore

// Global force refresh method for debugging
window.forceRefreshTenantState = () => {
  console.log('🔄 Global force refresh triggered')
  tenantSelectionStore.forceRefresh()

  // Also trigger Configure component refresh if available
  if (window.debugConfigure) {
    setTimeout(() => {
      console.log('🔄 Refreshing Configure component')
      window.debugConfigure()
    }, 100)
  }
}

export default tenantSelectionStore
